import React, { forwardRef, createContext, useContext, useId } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider, useFormContext } from 'react-hook-form';
import { cn } from '../../lib/utils';
import { Label } from './label';
const Form = FormProvider;
type FormFieldContextValue<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  name: TName;
};
const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);
const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return <FormFieldContext.Provider value={{
    name: props.name
  }}>
      <Controller {...props} />
    </FormFieldContext.Provider>;
};
// Jede FormItem vergibt eine eigene Kennung, über die Label, Feld, Beschreibung
// und Fehlermeldung zusammenfinden. Früher kam sie aus `register(name).id` —
// das gibt es in react-hook-form nicht, alle Felder hießen „undefined-form-item“.
type FormItemContextValue = {
  id: string;
};
const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);
const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const formContext = useFormContext();
  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }
  // Add a safety check here to prevent destructuring null/undefined
  if (!formContext) {
    return {
      id: '',
      name: fieldContext.name,
      formItemId: '',
      formDescriptionId: '',
      formMessageId: '',
      getFieldState: () => ({
        invalid: false,
        isDirty: false,
        isTouched: false,
        error: undefined
      }),
      formState: {
        errors: {}
      }
    };
  }
  const {
    name
  } = fieldContext;
  const {
    formState,
    getFieldState
  } = formContext;
  const fieldState = getFieldState(name, formState);
  const id = itemContext.id || `feld-${String(name).replace(/[^A-Za-z0-9_-]/g, '-')}`;
  const formItemId = `${id}-form-item`;
  const formDescriptionId = `${id}-form-item-description`;
  const formMessageId = `${id}-form-item-message`;
  return {
    id,
    name,
    formItemId,
    formDescriptionId,
    formMessageId,
    formState,
    getFieldState,
    fieldState
  };
};
const FormItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({
  className,
  ...props
}, ref) => {
  const id = useId();
  return <FormItemContext.Provider value={{
    id
  }}>
      <div ref={ref} className={cn('space-y-2', className)} {...props} />
    </FormItemContext.Provider>;
});
FormItem.displayName = 'FormItem';
const FormLabel = forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, ComponentPropsWithoutRef<typeof LabelPrimitive.Root>>(({
  className,
  ...props
}, ref) => {
  const {
    error,
    formItemId
  } = useFormField();
  return <Label ref={ref} className={cn(error && 'text-destructive', className)} htmlFor={formItemId} {...props} />;
});
FormLabel.displayName = 'FormLabel';
const FormControl = forwardRef<React.ElementRef<typeof Slot>, ComponentPropsWithoutRef<typeof Slot>>(({
  ...props
}, ref) => {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId
  } = useFormField();
  return <Slot ref={ref} id={formItemId} aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`} aria-invalid={!!error} {...props} />;
});
FormControl.displayName = 'FormControl';
const FormDescription = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({
  className,
  ...props
}, ref) => {
  const {
    formDescriptionId
  } = useFormField();
  return <p ref={ref} id={formDescriptionId} className={cn('text-sm text-muted-foreground', className)} {...props} />;
});
FormDescription.displayName = 'FormDescription';
const FormMessage = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({
  className,
  children,
  ...props
}, ref) => {
  const {
    error,
    formMessageId
  } = useFormField();
  const body = error ? String(error?.message) : children;
  if (!body) {
    return null;
  }
  return <p ref={ref} id={formMessageId} className={cn('text-sm font-medium text-destructive motion-safe:animate-fade-up', className)} {...props}>
      {body}
    </p>;
});
FormMessage.displayName = 'FormMessage';
export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField };